/**
 * Magic Link Authentication Service
 *
 * A professional, extensible service for handling magic link authentication flows.
 * This service abstracts the authentication logic and can be easily extended
 * for other authentication methods in the future.
 *
 * Architecture Features:
 * - Separation of concerns: Auth logic separated from UI
 * - Reusability: Can be used in different contexts
 * - Extensibility: Easy to add new auth methods
 * - Type-safe: Full TypeScript support
 * - Error handling: Comprehensive error states and messages
 *
 * @module magicLinkAuthService
 */

import { signIn, getSession } from "next-auth/react";
import { ROUTES_MAP } from "~/constants/routes";
import { MagicLinkAuthState, MagicLinkError } from "~/types-and-enums/authTypes";
import { TranslateType } from "~/types-and-enums/generalTypes";

/**
 * Configuration options for magic link authentication
 */
export type MagicLinkAuthConfig = {
  token: string;
  callbackUrl?: string;
  redirect?: boolean;
  translate?: TranslateType; // Optional translation function for i18n
};

/**
 * Result of magic link authentication attempt
 */
export type MagicLinkAuthResult = {
  success: boolean;
  error?: MagicLinkError;
  redirectUrl?: string;
};

/**
 * Parse error from NextAuth sign-in response
 * @param error - Error string from NextAuth
 * @param t - Optional translation function for i18n support
 */
const parseSignInError = (error: string | undefined, translate?: TranslateType): MagicLinkError => {
  // Helper to get translated or fallback message
  const getMessage = (key: string, fallback: string) => {
    return translate ? translate(key) : fallback;
  };

  if (!error) {
    return {
      message: getMessage("auth.magicLink.error.unknown", "An unknown error occurred during authentication"),
      state: MagicLinkAuthState.ERROR,
    };
  }

  // Try to extract message from JSON if error contains JSON string
  let errorMessage = error;
  try {
    // Check if error contains JSON (e.g., "400 : {...}")
    const jsonMatch = error.match(/\{.*\}/);
    if (jsonMatch) {
      const jsonData = JSON.parse(jsonMatch[0]);
      if (jsonData.message) {
        errorMessage = jsonData.message;
      }
    }
  } catch {
    // If JSON parsing fails, use original error
  }

  // Normalize error to lowercase for case-insensitive matching
  const errorLower = errorMessage.toLowerCase();
  const originalErrorLower = error.toLowerCase();

  // Check for expired token (case-insensitive) - check both extracted message and original error
  if (
    errorLower.includes("expired") ||
    errorLower.includes("expire") ||
    originalErrorLower.includes("expired") ||
    originalErrorLower.includes("expire")
  ) {
    return {
      message: getMessage("auth.magicLink.error.expired", "This magic link has expired. Please request a new one."),
      code: "EXPIRED_TOKEN",
      state: MagicLinkAuthState.EXPIRED_TOKEN,
    };
  }

  // Parse common error patterns (case-insensitive)
  if (error.includes("401") || errorLower.includes("invalid") || originalErrorLower.includes("invalid")) {
    return {
      message: getMessage(
        "auth.magicLink.error.invalidToken",
        "Invalid or expired magic link. Please request a new one."
      ),
      code: "INVALID_TOKEN",
      state: MagicLinkAuthState.INVALID_TOKEN,
    };
  }

  if (error.includes("403")) {
    return {
      message: getMessage("auth.magicLink.error.forbidden", "Access forbidden. Please contact support."),
      code: "FORBIDDEN",
      state: MagicLinkAuthState.ERROR,
    };
  }

  // Default error - use extracted message if available
  return {
    message: errorMessage || getMessage("auth.magicLink.error.default", "Authentication failed. Please try again."),
    state: MagicLinkAuthState.ERROR,
  };
};

/**
 * Validate and authenticate using a magic link token
 *
 * This function:
 * 1. Validates the token with the backend via NextAuth
 * 2. Creates a session if validation succeeds
 * 3. Returns appropriate error states for different failure scenarios
 *
 * @param config - Configuration for magic link authentication
 * @returns Promise resolving to authentication result
 *
 * @example
 * ```typescript
 * const result = await authenticateWithMagicLink({
 *   token: 'abc123',
 *   callbackUrl: '/dashboard',
 * });
 *
 * if (result.success) {
 *   // Redirect to result.redirectUrl or default location
 * } else {
 *   // Handle error: result.error
 * }
 * ```
 */
export const authenticateWithMagicLink = async (config: MagicLinkAuthConfig): Promise<MagicLinkAuthResult> => {
  const { token, callbackUrl = ROUTES_MAP.orders, translate } = config;

  // Helper to get translated or fallback message
  const getMessage = (key: string, fallback: string) => {
    return translate ? translate(key) : fallback;
  };

  // Validate token presence
  if (!token || token.trim() === "") {
    return {
      success: false,
      error: {
        message: getMessage("auth.magicLink.error.noToken", "No authentication token provided"),
        code: "NO_TOKEN",
        state: MagicLinkAuthState.INVALID_TOKEN,
      },
    };
  }

  try {
    // Attempt to sign in using NextAuth with the magic-link provider
    const result = await signIn("magic-link", {
      token,
      callbackUrl,
      redirect: false, // Don't redirect automatically, we'll handle it manually
    });

    // Check if sign-in was successful
    if (result?.ok) {
      // Get the session to access the redirect URL from the backend response
      const session = await getSession();
      const redirectUrl = session?.user?.redirect || result.url || callbackUrl;

      return {
        success: true,
        redirectUrl: redirectUrl,
      };
    }

    // Sign-in failed - parse and return error
    return {
      success: false,
      error: parseSignInError(result?.error ?? undefined, translate),
    };
  } catch (error) {
    // Handle unexpected errors
    console.error("Magic link authentication error:", error);

    return {
      success: false,
      error: {
        message:
          error instanceof Error
            ? error.message
            : getMessage("auth.magicLink.error.unexpected", "An unexpected error occurred. Please try again."),
        state: MagicLinkAuthState.ERROR,
      },
    };
  }
};

/**
 * Extract token from URL search parameters
 *
 * @param searchParams - URL search parameters
 * @returns Token string or null if not found
 */
export function extractTokenFromUrl(searchParams: URLSearchParams): string | null {
  return searchParams.get("token");
}

/**
 * Extract redirect URL from URL search parameters
 *
 * @param searchParams - URL search parameters
 * @returns Redirect URL or default orders page
 */
export function extractRedirectUrl(searchParams: URLSearchParams): string {
  const redirectUrl = searchParams.get("redirect") || searchParams.get("callbackUrl");

  // Validate redirect URL to prevent open redirect vulnerabilities
  if (redirectUrl) {
    // Only allow relative URLs or same-origin URLs
    try {
      const url = new URL(redirectUrl, window.location.origin);
      if (url.origin === window.location.origin) {
        return url.pathname + url.search;
      }
    } catch {
      // Invalid URL, use default
    }
  }

  return ROUTES_MAP.orders;
}

/**
 * Get user-friendly error message based on auth state
 *
 * @param state - Magic link authentication state
 * @param t - Optional translation function for i18n support
 * @returns User-friendly error message
 */
export function getErrorMessage(state: MagicLinkAuthState, translate?: TranslateType): string {
  // Helper to get translated or fallback message
  const getMessage = (key: string, fallback: string) => {
    return translate ? translate(key) : fallback;
  };

  switch (state) {
    case MagicLinkAuthState.INVALID_TOKEN:
      return getMessage("auth.magicLink.error.invalid", "This magic link is invalid. Please request a new one.");
    case MagicLinkAuthState.EXPIRED_TOKEN:
      return getMessage("auth.magicLink.error.expired", "This magic link has expired. Please request a new one.");
    case MagicLinkAuthState.ERROR:
      return getMessage(
        "auth.magicLink.error.contactSupport",
        "Authentication failed. Please try again or contact support."
      );
    default:
      return getMessage("auth.magicLink.error.unexpected", "An unknown error occurred. Please try again.");
  }
}

/**
 * Check if authentication can be retried based on error state
 *
 * @param state - Magic link authentication state
 * @returns True if retry is possible
 */
export function canRetryAuth(state: MagicLinkAuthState): boolean {
  return state === MagicLinkAuthState.ERROR;
}
